import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { 
  Home, 
  Users, 
  Heart, 
  Calendar, 
  Bell, 
  User, 
  LogOut,
  Menu,
  X,
  LayoutDashboard,
  Gift,
  HelpCircle,
  Music,
  UsersRound,
  Check,
  XCircle,
  Compass,
  BookOpen,
  ListMusic
} from 'lucide-react';
import { getInitials, cn } from '../lib/utils';
import { toast } from 'sonner';
import { AppTour } from './AppTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { href: '/feed', label: 'Community', icon: Home },
  { href: '/growth-groups', label: 'Groups', icon: UsersRound },
  { href: '/directory', label: 'Directory', icon: BookOpen },
  { href: '/music', label: 'Music', icon: Music },
  { href: '/calendar', label: 'Calendar', icon: Calendar },
  { href: '/roster', label: 'Roster', icon: Users },
  { href: '/order-of-service', label: 'Sunday', icon: ListMusic },
];

// Church logo (official)
import CHURCH_LOGO from '../assets/abc-logo.png';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
    <img src={CHURCH_LOGO} alt="Alkimos Baptist Church" className="h-10 w-auto" />
  </Link>
);

// Notification Bell Component
const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/unread-count`)
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch { /* silent */ }
  };
  
  const handleResponse = async (notification, action) => {
    try {
      await axios.post(`${API}/services/${notification.service_id}/respond`, { action });
      toast.success(`You have ${action === 'accept' ? 'accepted' : 'declined'} the service`);
      fetchNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };
  
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };
  
  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <div className="flex items-center justify-between p-2 border-b">
          <span className="font-medium">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[300px]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-church-teal"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No notifications
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={cn(
                  "p-3 border-b last:border-0 transition-colors",
                  !notif.read && "bg-church-teal/5"
                )}
                onClick={() => !notif.read && markAsRead(notif.id)}
              >
                <p className="font-medium text-sm">{notif.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{notif.message}</p>
                
                {notif.requires_action && notif.type === 'roster_reminder_48h' && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-green-600 hover:bg-green-700"
                      onClick={(e) => { e.stopPropagation(); handleResponse(notif, 'accept'); }}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); handleResponse(notif, 'decline'); }}
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Decline
                    </Button>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground mt-2">
                  {new Date(notif.created_at).toLocaleDateString()}
                </p>
              </div>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Navigation = ({ onStartTour }) => {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ href, label, icon: Icon, highlight }) => (
              <Link
                key={href}
                to={href}
                data-testid={`nav-${label.toLowerCase()}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  location.pathname === href
                    ? 'bg-church-teal text-white'
                    : highlight
                    ? 'text-church-teal border border-church-teal/30 hover:bg-church-teal/10'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-2">
            {/* Tour Button */}
            <Button
              variant="ghost"
              size="sm"
              className="hidden md:flex gap-2 rounded-full text-church-teal hover:text-church-teal-dark hover:bg-church-teal/10"
              onClick={onStartTour}
              data-testid="nav-tour-btn"
            >
              <Compass className="w-4 h-4" />
              Tour
            </Button>
            
            {/* Notification Bell */}
            <NotificationBell />
            
            {isAdmin && (
              <Link to="/admin" data-testid="admin-link">
                <Button variant="outline" size="sm" className="hidden md:flex gap-2 rounded-full">
                  <LayoutDashboard className="w-4 h-4" />
                  Admin
                </Button>
              </Link>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                  <Avatar className="h-10 w-10 ring-2 ring-church-teal/20">
                    <AvatarImage src={user.profile_image} alt={user.name} />
                    <AvatarFallback className="bg-church-teal text-white">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="flex items-center gap-2 p-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.profile_image} alt={user.name} />
                    <AvatarFallback className="bg-church-teal text-white text-sm">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{user.name}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2" data-testid="profile-menu-item">
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onStartTour} className="flex items-center gap-2" data-testid="tour-menu-item">
                  <Compass className="w-4 h-4" />
                  Take a Tour
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild className="md:hidden">
                    <Link to="/admin" className="flex items-center gap-2" data-testid="admin-menu-item">
                      <LayoutDashboard className="w-4 h-4" />
                      Admin Dashboard
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <nav className="flex flex-col gap-2 mt-8">
                  {navItems.map(({ href, label, icon: Icon }) => (
                    <Link
                      key={href}
                      to={href}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${label.toLowerCase()}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${
                        location.pathname === href
                          ? 'bg-church-teal text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

// Bottom Navigation Bar for Mobile
const BottomNav = () => {
  const location = useLocation();
  const { user, isAdmin } = useAuth();

  if (!user) return null;

  const bottomNavItems = [
    { href: '/feed', label: 'Home', icon: Home },
    { href: '/order-of-service', label: 'Sunday', icon: ListMusic },
    { href: '/roster', label: 'Roster', icon: Users },
    { href: '/music', label: 'Music', icon: Music },
    { href: '/profile', label: 'Profile', icon: User },
  ];

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 safe-area-bottom">
      <div className="flex items-center justify-around h-16">
        {bottomNavItems.map(({ href, label, icon: Icon, highlight }) => {
          const isActive = location.pathname === href;
          return (
            <Link
              key={href}
              to={href}
              data-testid={`bottom-nav-${label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                isActive ? 'text-church-teal' : highlight ? 'text-church-teal' : 'text-slate-400'
              }`}
            >
              <Icon className={`h-5 w-5 ${isActive ? 'stroke-[2.5px]' : ''}`} />
              <span className="text-[10px] mt-1 font-medium">{label}</span>
            </Link>
          );
        })}
      </div>
      {isAdmin && (
        <Link
          to="/admin"
          data-testid="bottom-nav-admin"
          className="absolute -top-12 right-4 bg-church-teal text-white p-3 rounded-full shadow-lg"
        >
          <LayoutDashboard className="h-5 w-5" />
        </Link>
      )}
    </nav>
  );
};

export const Layout = ({ children }) => {
  const { user } = useAuth();
  const [runTour, setRunTour] = useState(false);
  
  const handleStartTour = () => {
    setRunTour(true);
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Member Tour */}
      <AppTour 
        tourType="member" 
        run={runTour} 
        onComplete={() => setRunTour(false)} 
      />
      
      <Navigation onStartTour={handleStartTour} />
      <main className={`${user ? 'pb-24 md:pb-8' : 'pb-8'}`}>
        {children}
      </main>
      <footer className={`border-t border-slate-100 py-8 mt-auto ${user ? 'mb-16 md:mb-0' : ''}`}>
        <div className="container-app text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Alkimos Baptist Church. All rights reserved.</p>
          <p className="mt-1">Growing together in faith, hope, and love.</p>
        </div>
      </footer>
      <BottomNav />
    </div>
  );
};

export default Layout;
